import { type ReactNode, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  type FinancingFleetTier,
  type FinancingNeed,
  type FinancingRevenueTier,
  type ImageWithUpload,
  type OrganizationProfile,
  type OrgFinancingProfile,
  type OrgProfileUpdate,
} from '@hero/schema';
import {
  useOrgProfileAdminUpdateApi,
  useOrgProfileUpdateApi,
} from '@hero/api-hooks';
import type { ObjectId } from 'bson';
import { CheckIcon, FileTextIcon } from '@hero/web-icons';
import { cn } from '@hero/web-lib';
import { fileToBase64 } from '@hero/web-utils';
import { Input } from '../../../ui/Input/Input';
import { Label } from '../../../ui/Label/Label';
import { RadioGroup, RadioGroupItem } from '../../../ui/RadioGroup/RadioGroup';
import { Button } from '../../../ui/Button';
import { Spinner } from '../../../app/Spinner';
import { SelectAuto } from '../../../app/Select/SelectAuto';
import { PriceEdit } from '../../../app/Price/PriceEdit';
import { FileUploader } from '../../../app/File/FileUploader/FileUploader';

const FLEET_TIER_KEYS: FinancingFleetTier[] = [
  'lt_10',
  '11_20',
  '21_50',
  '51_100',
  '101_200',
  '201_500',
  'gt_500',
];

const REVENUE_TIER_KEYS: FinancingRevenueTier[] = [
  'lt_1m',
  '1m_3m',
  '3m_6m',
  '6m_8m',
  '8m_10m',
  'gt_10m',
];

const NEED_KEYS: FinancingNeed[] = [
  'invoicing',
  'trucks',
  'working_capital',
  'fuel',
  'other',
];

const FINANCIAL_STATEMENTS_ACCEPT: Record<string, string[]> = {
  'application/pdf': [],
};

type YesNo = 'yes' | 'no' | '';

/**
 * Local form state for the financing tab. Strings on numeric fields
 * mirror the pattern in the apply-form wizard so the same `''` -> "do
 * not submit" semantics apply.
 */
interface FinancingFormState {
  fleetTier: FinancingFleetTier | '';
  annualRevenueTier: FinancingRevenueTier | '';
  financingNeeds: FinancingNeed[];
  ownsAllTrucks: YesNo;
  trucksOwnedCount: string;
  hasLoans: YesNo;
  monthlyFinancingPayment: string;
  isProfitable: YesNo;
  financialStatements: ImageWithUpload[];
}

function emptyState(): FinancingFormState {
  return {
    fleetTier: '',
    annualRevenueTier: '',
    financingNeeds: [],
    ownsAllTrucks: '',
    trucksOwnedCount: '',
    hasLoans: '',
    monthlyFinancingPayment: '',
    isProfitable: '',
    financialStatements: [],
  };
}

function hydrate(profile: OrgFinancingProfile | undefined): FinancingFormState {
  if (!profile) return emptyState();
  return {
    fleetTier: profile.fleetTier,
    annualRevenueTier: profile.annualRevenueTier,
    financingNeeds: profile.financingNeeds,
    ownsAllTrucks: profile.ownsAllTrucks ? 'yes' : 'no',
    trucksOwnedCount:
      profile.trucksOwnedCount != null ? String(profile.trucksOwnedCount) : '',
    hasLoans: profile.hasLoans ? 'yes' : 'no',
    monthlyFinancingPayment:
      profile.monthlyFinancingPayment != null
        ? String(profile.monthlyFinancingPayment)
        : '',
    isProfitable:
      profile.isProfitable === undefined
        ? ''
        : profile.isProfitable
          ? 'yes'
          : 'no',
    financialStatements:
      (profile.financialStatements ?? []) as ImageWithUpload[],
  };
}

function buildPayload(
  state: FinancingFormState,
): NonNullable<OrgProfileUpdate['financingProfile']> | null {
  if (!state.fleetTier || !state.annualRevenueTier) return null;
  if (state.ownsAllTrucks === '') return null;
  if (state.ownsAllTrucks === 'no' && !state.trucksOwnedCount) return null;
  if (state.hasLoans === '') return null;
  if (state.hasLoans === 'yes' && !state.monthlyFinancingPayment) return null;

  const payload: NonNullable<OrgProfileUpdate['financingProfile']> = {
    fleetTier: state.fleetTier,
    annualRevenueTier: state.annualRevenueTier,
    financingNeeds: state.financingNeeds,
    ownsAllTrucks: state.ownsAllTrucks === 'yes',
    hasLoans: state.hasLoans === 'yes',
    financialStatements: state.financialStatements,
  };
  if (state.ownsAllTrucks === 'no') {
    payload.trucksOwnedCount = Number(state.trucksOwnedCount);
  }
  if (state.hasLoans === 'yes') {
    payload.monthlyFinancingPayment = Number(state.monthlyFinancingPayment);
  }
  if (state.isProfitable !== '') {
    payload.isProfitable = state.isProfitable === 'yes';
  }
  return payload;
}

export interface CarrierFinancingFormFieldsProps {
  profile: Partial<OrganizationProfile> | undefined;
  hideSubmit?: boolean;
  disabled?: boolean;
  isSubmitting?: boolean;
  /**
   * Submit handler — receives the validated, fully-populated
   * payload ready to drop into `OrgProfileUpdate.financingProfile`.
   * The fields component never calls any API hooks itself — all
   * persistence is the caller's responsibility. This makes the
   * form safe to reuse in shells that don't wire `<ApiProvider>`
   * (e.g. admin-panel, which uses `BaseApiService`).
   */
  onSubmit: (
    payload: NonNullable<OrgProfileUpdate['financingProfile']>,
  ) => void | Promise<void>;
}

/**
 * `CarrierFinancingFormFields` — pure UI + local state component
 * for editing `Org.financingProfile`. No API hooks — the caller
 * owns the mutation via the `onSubmit` prop and signals progress
 * via `isSubmitting`. Use this variant in shells that don't have
 * `<ApiProvider>` mounted (e.g. admin-panel). For carrier-dashboard
 * — which does have ApiProvider — the smarter wrapper
 * {@link CarrierFinancingForm} is the convenient default.
 */
export function CarrierFinancingFormFields({
  profile,
  hideSubmit,
  disabled,
  isSubmitting = false,
  onSubmit,
}: CarrierFinancingFormFieldsProps) {
  const { t } = useTranslation(['m-financing', 'm-organization']);

  const initial = useMemo(() => hydrate(profile?.financingProfile), [
    profile?._id,
    profile?.financingProfile,
  ]);
  const [state, setState] = useState<FinancingFormState>(initial);
  /**
   * Local `File[]` mirror of {@link state.financialStatements} for
   * fresh picks only. The persisted form value stays in
   * `state.financialStatements` as `ImageWithUpload[]` (the shape
   * the backend's `imageWithUploadSchema` expects: either
   * `{ url, id }` for already-uploaded entries or
   * `{ upload: <data-uri base64> }` for fresh picks). We keep the
   * `File` instances locally so `FileUploader` can render the
   * thumbnail / file metadata immediately on pick without
   * round-tripping through the API.
   *
   * Mirrors the `RhfImageField` pattern in
   * `apps/.../OrgProfileFields/shared/RhfImageField.tsx`.
   */
  const [statementFiles, setStatementFiles] = useState<File[]>([]);

  useEffect(() => {
    setState(initial);
    setStatementFiles([]);
  }, [initial]);

  const set = <K extends keyof FinancingFormState>(
    key: K,
    value: FinancingFormState[K],
  ) => setState(prev => ({ ...prev, [key]: value }));

  const toggleNeed = (need: FinancingNeed) => {
    const next = state.financingNeeds.includes(need)
      ? state.financingNeeds.filter(n => n !== need)
      : [...state.financingNeeds, need];
    set('financingNeeds', next);
  };

  /**
   * Convert each fresh `File` to a base64 data-URI string and stamp
   * it onto an `ImageWithUpload`. Persisted entries from the
   * profile keep their `{ url, id }` shape and round-trip through
   * the API as-is — `ImageService.upload` skips re-upload when no
   * `upload` field is set.
   *
   * Single-file today (we `.slice(0, 1)`) to match the existing
   * UX; the schema accepts an array so loosening to multi-file is
   * a one-line change here.
   */
  const handleStatementFiles = async (next: File[]) => {
    const picked = next.slice(0, 1);
    setStatementFiles(picked);
    if (picked.length === 0) {
      set('financialStatements', []);
      return;
    }
    try {
      const upload = await fileToBase64(picked[0]);
      set('financialStatements', [{ upload } as ImageWithUpload]);
    } catch (err) {
      console.error('Failed to read financial statement file:', err);
      setStatementFiles([]);
      set('financialStatements', []);
    }
  };

  /**
   * Surface the URL of an already-persisted statement to the
   * uploader so the carrier sees they have one on file (instead of
   * an empty dropzone) until they replace it. Fresh `File` picks
   * take precedence — once `statementFiles` is non-empty, the
   * uploader's own preview drives the UI.
   */
  const existingStatementUrls = useMemo(() => {
    if (statementFiles.length > 0) return [];
    return state.financialStatements
      .map(s => s.url)
      .filter((u): u is string => typeof u === 'string' && u.length > 0);
  }, [state.financialStatements, statementFiles.length]);

  const payload = useMemo(() => buildPayload(state), [state]);
  const canSubmit = !disabled && payload != null && !isSubmitting;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!payload) return;
    try {
      await onSubmit(payload);
    } catch (err) {
      // Surface to the console for dev; the caller is responsible
      // for user-facing error toasts.
      console.error('Failed to save financing profile:', err);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6" noValidate>
      <Section
        title={t('m-financing:apply_form.profile.sections.fleet.title')}
        description={t(
          'm-financing:apply_form.profile.sections.fleet.description',
        )}>
        <div className="space-y-5">
          <div className="max-w-md">
            <SelectAuto
              label={t('m-financing:apply_form.profile.fields.fleet_tier.title')}
              placeholder={t(
                'm-financing:apply_form.profile.fields.fleet_tier.placeholder',
              )}
              value={state.fleetTier}
              onChange={v => set('fleetTier', v as FinancingFleetTier)}
              options={FLEET_TIER_KEYS.map(key => ({
                value: key,
                label: t(`m-financing:fleet_tier.${key}`),
              }))}
              disabled={disabled}
              required
            />
          </div>

          <YesNoField
            label={t(
              'm-financing:apply_form.profile.fields.owns_all_trucks.title',
            )}
            hint={t(
              'm-financing:apply_form.profile.fields.owns_all_trucks.hint',
            )}
            value={state.ownsAllTrucks}
            onChange={v => {
              set('ownsAllTrucks', v);
              if (v === 'yes') set('trucksOwnedCount', '');
            }}
            yesLabel={t(
              'm-financing:apply_form.profile.fields.owns_all_trucks.yes',
            )}
            noLabel={t(
              'm-financing:apply_form.profile.fields.owns_all_trucks.no',
            )}
            disabled={disabled}
            required
          />

          {state.ownsAllTrucks === 'no' && (
            <div className="max-w-xs space-y-1.5">
              <Label htmlFor="trucks-owned-count">
                {t(
                  'm-financing:apply_form.profile.fields.trucks_owned_count.title',
                )}
                <RequiredMark />
              </Label>
              <Input
                id="trucks-owned-count"
                type="number"
                min={1}
                required
                disabled={disabled}
                value={state.trucksOwnedCount}
                onChange={e => set('trucksOwnedCount', e.target.value)}
                placeholder={t(
                  'm-financing:apply_form.profile.fields.trucks_owned_count.placeholder',
                )}
              />
            </div>
          )}
        </div>
      </Section>

      <Section
        title={t('m-financing:apply_form.profile.sections.financials.title')}
        description={t(
          'm-financing:apply_form.profile.sections.financials.description',
        )}>
        <div className="space-y-5">
          <div className="max-w-md">
            <SelectAuto
              label={t(
                'm-financing:apply_form.profile.fields.annual_revenue_tier.title',
              )}
              placeholder={t(
                'm-financing:apply_form.profile.fields.annual_revenue_tier.placeholder',
              )}
              value={state.annualRevenueTier}
              onChange={v =>
                set('annualRevenueTier', v as FinancingRevenueTier)
              }
              options={REVENUE_TIER_KEYS.map(key => ({
                value: key,
                label: t(`m-financing:revenue_tier.${key}`),
              }))}
              disabled={disabled}
              required
            />
          </div>

          <YesNoField
            label={t(
              'm-financing:apply_form.profile.financials.is_profitable.title',
            )}
            value={state.isProfitable}
            onChange={v => set('isProfitable', v)}
            yesLabel={t(
              'm-financing:apply_form.profile.financials.is_profitable.yes',
            )}
            noLabel={t(
              'm-financing:apply_form.profile.financials.is_profitable.no',
            )}
            disabled={disabled}
          />

          <YesNoField
            label={t('m-financing:apply_form.profile.fields.has_loans.title')}
            value={state.hasLoans}
            onChange={v => set('hasLoans', v)}
            yesLabel={t('m-financing:apply_form.profile.fields.has_loans.yes')}
            noLabel={t('m-financing:apply_form.profile.fields.has_loans.no')}
            disabled={disabled}
            required
          />

          {state.hasLoans === 'yes' && (
            <div className="max-w-sm space-y-1.5">
              <Label htmlFor="monthly-financing-payment">
                {t(
                  'm-financing:apply_form.profile.fields.monthly_financing_payment.title',
                )}
                <RequiredMark />
              </Label>
              <PriceEdit
                name="monthly-financing-payment"
                disabled={disabled}
                value={
                  state.monthlyFinancingPayment === ''
                    ? undefined
                    : Number(state.monthlyFinancingPayment)
                }
                onChange={v =>
                  set('monthlyFinancingPayment', v == null ? '' : String(v))
                }
                placeholder={t(
                  'm-financing:apply_form.profile.fields.monthly_financing_payment.placeholder',
                )}
              />
            </div>
          )}

          <div className="space-y-2">
            <Label>
              {t('m-financing:apply_form.profile.financials.statements.title')}
            </Label>
            <FileUploader
              files={statementFiles}
              onChange={handleStatementFiles}
              existingUrls={existingStatementUrls}
              label={t(
                'm-financing:apply_form.profile.financials.statements.upload_label',
              )}
              emptyIcon={<FileTextIcon className="size-5 text-gray-400" />}
              variant="compact"
              acceptedFileTypes={FINANCIAL_STATEMENTS_ACCEPT}
              hint={t(
                'm-financing:apply_form.profile.financials.statements.upload_hint',
              )}
              disabled={disabled}
            />
          </div>
        </div>
      </Section>

      <Section
        title={t(
          'm-financing:apply_form.profile.sections.future_needs.title',
        )}
        description={t(
          'm-financing:apply_form.profile.sections.future_needs.description',
        )}>
        <div className="flex flex-wrap gap-2">
          {NEED_KEYS.map(need => {
            const isSelected = state.financingNeeds.includes(need);
            return (
              <button
                key={need}
                type="button"
                disabled={disabled}
                onClick={() => toggleNeed(need)}
                aria-pressed={isSelected}
                className={cn(
                  'rounded-full border px-3 py-1.5 text-xs font-medium transition-colors disabled:opacity-50',
                  isSelected
                    ? 'border-emerald-300 bg-emerald-50 text-emerald-800'
                    : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300',
                )}>
                {t(`m-financing:financing_need.${need}`)}
              </button>
            );
          })}
        </div>
      </Section>

      {!hideSubmit ? (
        <div className="flex items-center justify-end">
          <Button type="submit" disabled={!canSubmit}>
            {isSubmitting ? (
              <Spinner />
            ) : (
              <span className="flex items-center gap-2">
                <CheckIcon className="size-4" />
                {t('m-organization:buttons.save')}
              </span>
            )}
          </Button>
        </div>
      ) : null}
    </form>
  );
}

export interface CarrierFinancingFormProps {
  profile: Partial<OrganizationProfile> | undefined;
  hideSubmit?: boolean;
  disabled?: boolean;
  /**
   * When provided, writes go through `useOrgProfileAdminUpdateApi`
   * targeting the supplied org id (super-admin only). When omitted
   * (default), writes go through `useOrgProfileUpdateApi` for the
   * current user's own org. The form body is identical in both
   * cases — only the destination hook changes.
   *
   * Both hooks require a mounted `<ApiProvider>`. In shells that
   * don't have one (e.g. admin-panel which uses `BaseApiService`),
   * use {@link CarrierFinancingFormFields} directly and supply
   * your own `onSubmit`.
   */
  targetOrgId?: ObjectId;
  onSaved?: (patch: {
    financingProfile: NonNullable<OrgProfileUpdate['financingProfile']>;
  }) => void;
}

/**
 * `CarrierFinancingForm` — convenience wrapper around
 * {@link CarrierFinancingFormFields} that drives the API call
 * itself via `useOrgProfileUpdateApi` (own org) or
 * `useOrgProfileAdminUpdateApi` (target org, super-admin). Used by
 * the carrier dashboard where `<ApiProvider>` is already mounted.
 */
export function CarrierFinancingForm({
  profile,
  hideSubmit,
  disabled,
  targetOrgId,
  onSaved,
}: CarrierFinancingFormProps) {
  const ownUpdate = useOrgProfileUpdateApi();
  const adminUpdate = useOrgProfileAdminUpdateApi();
  const isSubmitting = targetOrgId ? adminUpdate.isPending : ownUpdate.isPending;

  const handleSubmit = async (
    payload: NonNullable<OrgProfileUpdate['financingProfile']>,
  ) => {
    if (targetOrgId) {
      await adminUpdate.mutateAsync({
        orgId: targetOrgId,
        financingProfile: payload,
      });
    } else {
      await ownUpdate.mutateAsync({ financingProfile: payload });
    }
    onSaved?.({ financingProfile: payload });
  };

  return (
    <CarrierFinancingFormFields
      profile={profile}
      hideSubmit={hideSubmit}
      disabled={disabled}
      isSubmitting={isSubmitting}
      onSubmit={handleSubmit}
    />
  );
}

function RequiredMark() {
  return <span className="text-destructive ms-1">*</span>;
}

function Section({
  title,
  description,
  children,
}: {
  title: string;
  description?: string;
  children: ReactNode;
}) {
  return (
    <section className="space-y-4 rounded-xl border border-gray-200 bg-gray-50/50 p-4">
      <div>
        <h4 className="text-sm font-semibold text-gray-900">{title}</h4>
        {description ? (
          <p className="mt-0.5 text-xs text-gray-500">{description}</p>
        ) : null}
      </div>
      <div className="space-y-3">{children}</div>
    </section>
  );
}

function YesNoField({
  label,
  hint,
  value,
  onChange,
  yesLabel,
  noLabel,
  required,
  disabled,
}: {
  label: string;
  hint?: string;
  value: YesNo;
  onChange: (value: YesNo) => void;
  yesLabel: string;
  noLabel: string;
  required?: boolean;
  disabled?: boolean;
}) {
  return (
    <div className="space-y-2">
      <Label>
        {label}
        {required ? <RequiredMark /> : null}
      </Label>
      <RadioGroup
        value={value}
        onValueChange={v => onChange(v as YesNo)}
        disabled={disabled}
        className="grid grid-cols-1 gap-2 sm:grid-cols-2">
        <Label
          className={cn(
            'flex cursor-pointer items-center gap-2.5 rounded-lg border px-3 py-2.5 text-sm font-normal transition-colors hover:bg-white',
            value === 'yes'
              ? 'border-emerald-300 bg-emerald-50/80'
              : 'border-gray-200 bg-white',
          )}>
          <RadioGroupItem value="yes" />
          <span className="text-start leading-snug">{yesLabel}</span>
        </Label>
        <Label
          className={cn(
            'flex cursor-pointer items-center gap-2.5 rounded-lg border px-3 py-2.5 text-sm font-normal transition-colors hover:bg-white',
            value === 'no'
              ? 'border-emerald-300 bg-emerald-50/80'
              : 'border-gray-200 bg-white',
          )}>
          <RadioGroupItem value="no" />
          <span className="text-start leading-snug">{noLabel}</span>
        </Label>
      </RadioGroup>
      {hint ? <p className="text-xs text-gray-500">{hint}</p> : null}
    </div>
  );
}
